import React, { useRef, useEffect, useState } from 'react'
import Button from 'srcReact/components/common2/Button'
import styles from './styles.module.scss'
import { useTranslation } from 'react-i18next'

import { useSelector, useDispatch } from 'react-redux'
import RequiredStar from 'srcReact/components/common2/RequiredStar'
import RichTextEditor from 'srcReact/components/common2/RichTextEditor'
import Spinner from 'srcReact/components/common2/Spinner'
import { appConstantSettings } from 'srcReact/appConstantSettings'
import { createUpdateGroupActions } from 'srcReact/GroupPage'
import ErrorMessage from 'srcReact/components/common2/ErrorMessage'

export default function DescriptionTextArea({
  show,
  hideNextButton,
  nextSectionVisible,
  containerCss,
  verticalLineCss,
  infoCss,
  innerContainerCss,
  titleCss,
  buttonWrapperCss,
  editMode = false
}) {
  const [focusedMode, setFocusedMode] = useState(false)
  const { t } = useTranslation()
  const charLimit = appConstantSettings.charLimit
  const maxLines = 15
  const containerRef = useRef(null)
  const editorRef = useRef('groupdescription')
  useEffect(() => {
    if (!editMode) {
      containerRef.current.scrollIntoView(true, { behavior: 'smooth' })
    }
  }, [show])

  const dispatch = useDispatch()
  const {
    description: globalGroupDescription,
    isGeneralGroup,
    editSuccess,
    loading,
    groupInfo
  } = useSelector((state) => state.createUpdateGroup)
  const [infoForEditReceived, setInfoForEditReceived] = useState(false)
  const [groupDescription, setGroupDescription] = useState({
    name: 'groupDescription',
    value: globalGroupDescription ? globalGroupDescription.value : '',
    hasError: false,
    errorMessage: '',
    isRequired: true
  })

  useEffect(() => {
    setGroupDescription({ ...groupDescription, value: '', hasError: false, errorMessage: '' })
  }, [])

  useEffect(() => {
    if (editMode && editSuccess && focusedMode) {
      setFocusedMode(false)
      dispatch(createUpdateGroupActions.setDescription(groupDescription))
    }
  }, [editSuccess])

  useEffect(() => {
    if (editMode && groupInfo && !infoForEditReceived) {
      setInfoForEditReceived(true)
      setGroupDescription({
        name: 'groupDescription',
        value: groupInfo ? groupInfo.description : '',
        hasError: false,
        errorMessage: '',
        isRequired: true
      })
    }
  }, [editMode, groupInfo])

  const handleChange = (currentContent) => {
    setGroupDescription({
      ...groupDescription,
      value: currentContent,
      hasError: false,
      errorMessage: ''
    })
  }

  const handleErrorFromInputArea = (error) => {
    //setErrorFromInputArea(error)
    if (error) {
      setGroupDescription({
        ...groupDescription,
        hasError: true,
        errorMessage: error
      })
    } else {
      setGroupDescription({
        ...groupDescription,
        hasError: false,
        errorMessage: ''
      })
    }
  }

  const validate = () => {
    if (groupDescription.isRequired && groupDescription.value.length === 0) {
      setGroupDescription({
        ...groupDescription,
        hasError: true,
        errorMessage: t('error.required', { field: t(`formlabel.${groupDescription.name}`) })
      })
      return false
    }
    setGroupDescription({
      ...groupDescription,
      hasError: false,
      errorMessage: ''
    })
    return true
  }

  const handleFocus = () => {
    setFocusedMode(true)
  }
  const handleBlur = () => {
    setFocusedMode(false)
  }
  const handleCancel = () => {
    // setContent(globalGroupDescription ? globalGroupDescription.value : '')

    setGroupDescription({
      name: 'groupDescription',
      value: globalGroupDescription && globalGroupDescription.value,
      hasError: false,
      errorMessage: '',
      isRequired: true
    })
    setFocusedMode(false)
  }

  const handleSave = () => {
    if (validate()) {
      dispatch(createUpdateGroupActions.editGroup(groupInfo.id, { description: groupDescription.value }))
    }
  }

  const sanitize = () => {
    setGroupDescription({
      ...groupDescription,
      value: sanitizeHtml(groupDescription.value, sanitizeConf)
    })
    // setHtml(sanitizeHtml(html, sanitizeConf))
  }

  const handleNext = () => {
    if (groupDescription && validate()) {
      if (isGeneralGroup) {
        dispatch(createUpdateGroupActions.showAdvSetting())
        dispatch(createUpdateGroupActions.setFormAsCompleted())
      } else {
        dispatch(createUpdateGroupActions.showInterests())
      }
      dispatch(createUpdateGroupActions.setDescription(groupDescription))
    }
  }
  const jumpToNextAfterUpdate = () => {
    if (groupDescription && !groupDescription.hasError) {
      const nextContainer = document.querySelector('#interestContainer')
      nextContainer.scrollIntoView(true, { behavior: 'smooth' })
      dispatch(createUpdateGroupActions.setDescription(groupDescription))
    }
  }

  useEffect(() => {
    if (!editMode) {
      dispatch(createUpdateGroupActions.setDescription(groupDescription))
    }
  }, [groupDescription])
  return (
    <div
      id='descriptionContainer'
      ref={containerRef}
      className={`${containerCss} ${!nextSectionVisible && !editMode && styles.container} ${
        show ? styles.show : styles.hide
      }`}>
      <div class={verticalLineCss} />
      {!editMode && <p className={infoCss}>{t('group.createGroup.groupDescription.title1')}</p>}

      <div className={innerContainerCss}>
        <label className={titleCss}>
          {editMode ? t('group.editGroup.groupDescription') : t('group.createGroup.groupDescription.title2')}
          {groupDescription.isRequired && <RequiredStar />}
        </label>

        <div
          className={`${styles.editorContainer} ${focusedMode ? styles.editorContainerFocused : ''}`}
          onFocus={handleFocus}>
          <RichTextEditor
            value={editMode && groupDescription.value}
            showTextFormatter={true}
            showEmojiPicker={false}
            showFilePicker={false}
            placeholder={t('group.createGroup.groupDescription.placeholder')}
            handleChange={handleChange}
            //handleReturn={handleNext}
            containerCSS={styles.richTextAreaWrapper}
            charLimit={charLimit}
            maxLines={maxLines}
            handleInputError={handleErrorFromInputArea}
            editorRef={editorRef}
            editorId={`groupdesc`}
            errorMessageRequired={t('error.required', { field: t(`formlabel.${groupDescription.name}`) })}
            errorMessageLimitExceed={t('group.createGroup.groupDescription.charLimit', {
              charLimit: charLimit
            })}
            keepFormatOnPopulate={true}
          />
        </div>

        {groupDescription && groupDescription.hasError && (
          <ErrorMessage text={groupDescription.errorMessage} className={styles.error} />
        )}

        <div className={buttonWrapperCss}>
          {editMode ? (
            focusedMode || groupDescription.hasError ? (
              <React.Fragment>
                <Button style='tertiary' type='button' handleClick={handleCancel} text={t('global.cancel')} />
                <Button
                  type='button'
                  handleClick={handleSave}
                  text={loading ? <Spinner isWhite={true} /> : t('global.save')}
                />
              </React.Fragment>
            ) : null
          ) : !hideNextButton ? (
            <Button
              disabled={groupDescription && groupDescription.value.length === 0}
              handleClick={nextSectionVisible ? jumpToNextAfterUpdate : handleNext}
              text={nextSectionVisible ? t('group.createGroup.btnNext') : t('group.createGroup.btnNext')}
            />
          ) : null}
        </div>
      </div>
    </div>
  )
}
